import { Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Stack, TableCell, styled } from '@mui/material';
import { DeleteIcon, EditIcon, ExtendedMenuIcon } from '@synergy/theme-next';
import { DragIndicatorOutlined } from '@mui/icons-material';
import { DuplicateIcon } from '@synergy/theme';
import { Field, FieldProps } from 'formik';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { MouseEvent, useState } from 'react';
import { PaloAmount, PaloQuantityCell } from '@synergy/components-palo';
import { PoolNumberTextfield, PoolTypography } from '@synergy/components-next';
import { converter } from '@synergy/utils';
import { tokens } from '@synergy/theme-palo';

import { FinancialCalculations } from '../../../../../../common/utils/financial-calculations';
import { InvoiceGenericItem } from '../../../../models/invoice-generic-item';
import { PositionSelection, PositionSelectionBox } from '../../../../../../common/components/palo/position-selection';
import { ROW_HANDLE_SX, RowDrag, rowDragHandleProps } from '../../../../../../common/components/palo/position-row-drag';
import { RowPosition } from './detail-positions-table';
import { TaxRate } from '../../../../../../common/models/tax-rate';
import { TaxRateAutocomplete } from '../../../../../../common/components/tax-rate-autocomplete/tax-rate-autocomplete';
import { bestLocaleSelector } from '../../../../../../common/selectors/locale-selectors';
import { positionFieldNames } from '../../../models/positions-form-values';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';

export interface DetailPositionsTableGenericItemRowProps {
    index: number;
    item: InvoiceGenericItem;
    readonly: boolean;
    taxRates: TaxRate[];
    currency: string;
    position: RowPosition;
    onTaxRateSelected: (index: number, taxRate: TaxRate | null) => void;
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    padding: theme.spacing(0, 1),
}));

export const DetailPositionsTableGenericItemRow = (props: DetailPositionsTableGenericItemRowProps) => {
    const { index, item, taxRates, position, readonly = false, currency, onTaxRateSelected } = props;
    const locale = useAppSelector(bestLocaleSelector);
    const prefix = positionFieldNames.allItems + '.' + index;

    return (
        <>
            <StyledTableCell>
                <PoolTypography typography="body2">{item.title}</PoolTypography>
                <PoolTypography typography="label">
                    <Box
                        color="grey[800]"
                        dangerouslySetInnerHTML={{
                            __html: converter.makeHtml(item.description ?? ''),
                        }}
                        sx={{ '& p': { margin: 0 } }}
                    />
                </PoolTypography>
            </StyledTableCell>
            <StyledTableCell>
                <PaloQuantityCell>
                    <Field name={prefix + '.quantity'}>
                        {({ field, form, meta }: FieldProps<number>) => (
                            <PoolNumberTextfield
                                {...field}
                                id={field.name}
                                type="dense"
                                label={<FormattedMessage id="app.enum.piece" />}
                                error={!!meta.touched && !!meta.error}
                                helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                                autoFocus={position === 'last'}
                                textAlign="right"
                            />
                        )}
                    </Field>
                </PaloQuantityCell>
            </StyledTableCell>
            <StyledTableCell>
                <Field name={prefix + '.price'}>
                    {({ field, meta }: FieldProps<number>) => (
                        <PoolNumberTextfield
                            {...field}
                            value={field.value}
                            id={field.name}
                            type="dense"
                            error={!!meta.touched && !!meta.error}
                            helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                            readonly={readonly}
                            fullWidth={true}
                            currency={currency}
                            currencySymbol={false}
                            locale={locale}
                            textAlign="right"
                            sx={{ width: 110 }}
                        />
                    )}
                </Field>
            </StyledTableCell>
            <StyledTableCell>
                <Field name={prefix + '.discount'}>
                    {({ field, meta }: FieldProps<number>) => (
                        <PoolNumberTextfield
                            {...field}
                            onBlur={field.onBlur}
                            value={field.value}
                            type="dense"
                            error={!!meta.touched && !!meta.error}
                            helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                            readonly={readonly}
                            suffix=" %"
                            textAlign="right"
                            sx={{ width: 75 }}
                        />
                    )}
                </Field>
            </StyledTableCell>
            <StyledTableCell>
                <TaxRateAutocomplete
                    display="short"
                    name={prefix + '.taxRate'}
                    disabled={readonly}
                    taxRates={taxRates}
                    onValueChange={taxRate => onTaxRateSelected(index, taxRate)}
                    showLabel={false}
                    textAlign="right"
                />
            </StyledTableCell>
            <StyledTableCell>
                <Field name={prefix + '.tax'}>
                    {({ field, meta }: FieldProps<number>) => (
                        <PoolNumberTextfield
                            {...field}
                            type="dense"
                            error={!!meta.touched && !!meta.error}
                            helperText={meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null}
                            readonly={readonly}
                            suffix=" %"
                            textAlign="right"
                            sx={{ width: 75 }}
                        />
                    )}
                </Field>
            </StyledTableCell>
            <StyledTableCell>
                <Box sx={{ whiteSpace: 'pre', width: 97, textAlign: 'right' }}>
                    <PaloAmount value={FinancialCalculations.getNetTotal(item)} />
                </Box>
            </StyledTableCell>
        </>
    );
};
